import { ExternalLink } from 'lucide-react';
import { pmLaunchHref, usePmLaunchStatus } from '../pm-launch/usePmLaunchStatus';
import { isLaunchId } from '../../services/hiringRecruitmentApi';
import { BUDGETS_PAGE_WORDS, budgetsPageLookFor } from './hiringText';
import { HIRING_BUTTONS } from './hiringLooks';

/**
 * "Open Manage Budgets": the Manage Budgets page of 1Office Project-Management, in a new
 * tab, with the person already signed in.
 *
 * The hiring screens show counts only. A budget's openings with their candidates by name,
 * and adding positions, reducing positions and deleting an opening, are on that page.
 *
 * WHO SEES IT. The server decides, by the same question the Attrition link asks
 * (usePmLaunchStatus): a super admin, or a holder of "Raise & cancel hiring" for this
 * project (since 14 Oct 2026; before, Hiring (manage) - the Stores tick),
 * signed in to ChannelPlex as a 1Office employee, while launch links are not stopped on
 * the server. While that question is unanswered, answers no, or fails, NOTHING is drawn:
 * not the control and not the line beside it. So a screen that holds this is, for
 * everybody else, exactly the screen it was.
 *
 * WHAT IT OPENS. The web's own "Opening…" page (/open/project-management/manage-budgets),
 * which asks for a one-time 1Office sign-in and moves on to it. A plain link, so the
 * browser opens the tab at once and does not block it as a pop-up.
 *
 * ON THE EXACT OPENING (1Office's reply of 15 October 2026, B4): given the budget's number
 * (and the opening's), the address carries them -
 * /open/project-management/manage-budgets?project=..&budget=..&opening=.. - and the opening
 * page passes them on; 1Office then opens that budget's Openings window with the opening
 * highlighted, when the person can see that budget in Project-Management (and the page as
 * before otherwise). Each is a whole number from 1 to 2147483647, or it is left out; an
 * opening without its budget is left out too (1Office needs the budget). They grant
 * nothing: who is offered the link is the server's answer, exactly as without them.
 *
 * Every word is in hiringText (BUDGETS_PAGE_WORDS, budgetsPageLookFor).
 */

const LOOKS = {
  /** A quiet text link: on a card, beside a button, under a refusal. */
  link: 'inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:underline',
  /** In a row of buttons: the look of the plain button beside it. */
  button: 'inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50',
  /**
   * Beside the Hiring dashboard's title: the hiring screens' outlined button
   * (HIRING_BUTTONS.secondary), class for class, with room for the new-tab mark.
   */
  secondary: `${HIRING_BUTTONS.secondary} gap-2`,
} as const;

/**
 * The address of the opening page: the project, and - when its number is a valid one - the
 * budget to open Manage Budgets on and the opening to highlight in it.
 */
export const manageBudgetsHref = (projectId: number, budgetId?: number | null, openingId?: number | null): string => {
  const base = pmLaunchHref(projectId, 'manage-budgets');
  if (!isLaunchId(budgetId)) return base;
  return `${base}&budget=${budgetId}${isLaunchId(openingId) ? `&opening=${openingId}` : ''}`;
};

export function ManageBudgetsLink({
  projectId, active = true, look = 'link', budgetId, budgetName, openingId, className, label, hover,
}: {
  projectId: number | null | undefined;
  /** False for a person the screen already knows may not raise hiring: the server is then not asked at all. */
  active?: boolean;
  look?: keyof typeof LOOKS;
  /** The budget's number: Manage Budgets then opens on that budget (with `openingId` highlighted). */
  budgetId?: number | null;
  /** On an opening's card, and on a request still being raised: the budget to look for on the page, when it is known. */
  budgetName?: string | null;
  /** The opening to look for under that budget - and, with `budgetId`, to highlight there - when it has a number. */
  openingId?: number | null;
  /** Where the control sits in its parent (for example `self-start` in a column). Never its look. */
  className?: string;
  /** The words on it, where the screen says them otherwise ("Open in Manage Budgets" in an opening's foot). */
  label?: string;
  /** Its hover, where the screen says it otherwise (the Hiring dashboard's title row). */
  hover?: string;
}) {
  const { available } = usePmLaunchStatus(projectId, 'manage-budgets', active);
  if (!available || !projectId) return null;

  const lookFor = budgetsPageLookFor(budgetName, openingId);
  const link = (
    <a
      href={manageBudgetsHref(projectId, budgetId, openingId)}
      target="_blank"
      rel="noopener noreferrer"
      title={hover ?? BUDGETS_PAGE_WORDS.hover}
      className={`${LOOKS[look]}${!lookFor && className ? ` ${className}` : ''}`}
    >
      {label ?? BUDGETS_PAGE_WORDS.open}
      <ExternalLink className={look === 'link' ? 'h-3.5 w-3.5' : 'h-4 w-4'} aria-hidden="true" />
      {/* The space is inside the hidden words: read out as "Open Manage Budgets (opens in a new tab)". */}
      <span className="sr-only">{` (${BUDGETS_PAGE_WORDS.newTab})`}</span>
    </a>
  );
  if (!lookFor) return link;
  return (
    <p className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-600${className ? ` ${className}` : ''}`}>
      {link}
      <span>{lookFor}</span>
    </p>
  );
}

export default ManageBudgetsLink;
